import { Component, signal, OnInit, AfterViewInit, inject, ViewChild, ElementRef, OnDestroy, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { trigger, transition, style, query, group, animate } from '@angular/animations';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { StaffLayoutComponent } from "./staff-layout/staff-layout.component";
import { LogoutConfirmationModalComponent } from './shared/components/logout-confirmation-modal.component';
import { StaffLogoutService } from './core/services/staff-logout.service';
import { KioskService } from './core/services/kiosk.service';
import { LoadingService } from './core/services/loading.service';
import { TapSoundService } from './core/services/tap-sound.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, StaffLayoutComponent, LogoutConfirmationModalComponent, CommonModule],
  templateUrl: './app.html',
  styleUrl: './app.css',
  animations: [
    trigger('routeAnimation', [
      // Sign-in page: centered scale animation
      transition('* => SignIn', [
        query(':enter, :leave', [
          style({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          })
        ], { optional: true }),
        // Invisible leaving page must not steal clicks from the new page.
        query(':leave', [
          style({ pointerEvents: 'none' })
        ], { optional: true }),
        query(':enter', [
          style({
            transform: 'scale(0.9)',
            opacity: 0,
            transformOrigin: 'center center',
            pointerEvents: 'auto'
          })
        ], { optional: true }),
        group([
          query(':leave', [
            animate('300ms ease-in-out', style({
              opacity: 0,
              transform: 'scale(0.95)',
              transformOrigin: 'center center',
              pointerEvents: 'none'
            }))
          ], { optional: true }),
          query(':enter', [
            animate('400ms ease-out', style({
              transform: 'scale(1)',
              opacity: 1,
              transformOrigin: 'center center',
              position: 'relative',
              pointerEvents: 'auto'
            }))
          ], { optional: true })
        ])
      ]),
      // Sign-in page leaving: centered scale animation for both pages
      transition('SignIn => *', [
        query(':enter, :leave', [
          style({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          })
        ], { optional: true }),
        query(':leave', [
          style({ pointerEvents: 'none' })
        ], { optional: true }),
        query(':enter', [
          style({
            transform: 'scale(0.9)',
            opacity: 0,
            transformOrigin: 'center center',
            pointerEvents: 'auto'
          })
        ], { optional: true }),
        group([
          query(':leave', [
            animate('300ms ease-in-out', style({
              opacity: 0,
              transform: 'scale(0.9)',
              transformOrigin: 'center center',
              pointerEvents: 'none'
            }))
          ], { optional: true }),
          query(':enter', [
            animate('400ms ease-out', style({
              transform: 'scale(1)',
              opacity: 1,
              transformOrigin: 'center center',
              position: 'relative',
              pointerEvents: 'auto'
            }))
          ], { optional: true })
        ])
      ]),
      transition('* <=> NoAnimation', []),
      // Default: slide animation for other routes
      transition('* => *', [
        query(':enter, :leave', [
          style({
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          })
        ], { optional: true }),
        query(':leave', [
          style({ pointerEvents: 'none' })
        ], { optional: true }),
        query(':enter', [
          style({
            transform: 'translateX(100%)',
            opacity: 0,
            pointerEvents: 'auto'
          })
        ], { optional: true }),
        group([
          query(':leave', [
            animate('900ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({
              transform: 'translateX(-100%)',
              opacity: 0,
              pointerEvents: 'none'
            }))
          ], { optional: true }),
          query(':enter', [
            animate('900ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({
              transform: 'translateX(0%)',
              opacity: 1,
              position: 'relative',
              pointerEvents: 'auto'
            }))
          ], { optional: true })
        ])
      ])
    ])
  ]
})
export class App implements OnInit, AfterViewInit, OnDestroy {
  protected readonly title = signal('customer-portal');
  protected readonly loadingService = inject(LoadingService);
  protected readonly staffLogoutService = inject(StaffLogoutService);
  private readonly kioskService = inject(KioskService);
  private readonly tapSoundService = inject(TapSoundService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private clickHandler?: (event: MouseEvent) => void;
  showStaffLayout = signal(true);
  @ViewChild('routeContainer', { static: false }) routeContainer?: ElementRef<HTMLElement>;

  ngOnInit(): void {
    // Enable dev tools access after deployment
    
  
    
    // Initialize kiosk mode
    this.kioskService.initialize();
    
    // Initialize global click sound handler
    this.initializeClickSound();
    
    // Check initial route
    this.updateStaffLayoutVisibility();
    
    // Listen to route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateStaffLayoutVisibility();
      this.scrollToTopOnNavigation();
      // Reset scroll state on route change
      this.resetScrollState();
      // Enable scrolling after route change
      setTimeout(() => {
        this.enableScrolling();
      }, 100);
    });

    // Listen for network status changes
    if (isPlatformBrowser(this.platformId)) {
      this.setupNetworkStatusListener();
    }
  }

  private setupNetworkStatusListener(): void {
    // Check initial network status
    if (!navigator.onLine) {
      this.router.navigate(['/error'], {
        state: { error: { type: 'network', message: 'No internet connection' } }
      });
    }

    // Listen for online/offline events
    window.addEventListener('online', () => {
      // Network came back - user can retry
      // Don't auto-navigate away, let them decide
    });

    window.addEventListener('offline', () => {
      // Network went offline - navigate to error page if not already there
      if (!this.router.url.includes('/error')) {
        this.router.navigate(['/error'], {
          state: { error: { type: 'network', message: 'Connection lost' } }
        });
      }
    });
  }

  ngAfterViewInit(): void {
    // Enable scrolling after initial render
    setTimeout(() => {
      this.enableScrolling();
    }, 100);
  }

  private resetScrollState(): void {
    if (this.routeContainer?.nativeElement) {
      this.routeContainer.nativeElement.classList.remove('scroll-enabled', 'has-overflow');
    }
  }

  private enableScrolling(): void {
    // Wait for content and animations to fully render
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.checkAndUpdateScrollbar();
        }, 900); // Wait for animations to complete (600ms + buffer)
      });
    });
  }

  private checkAndUpdateScrollbar(): void {
    if (this.routeContainer?.nativeElement) {
      const container = this.routeContainer.nativeElement;
      
      // Add scroll-enabled class to enable scrolling functionality
      container.classList.add('scroll-enabled');
      
      // Check overflow function - more reliable detection
      const checkOverflow = () => {
        // Force a reflow to ensure accurate measurements
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        container.offsetHeight;
        
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        // Check if content actually overflows (content is hidden)
        const hasOverflow = scrollHeight > clientHeight;
        
        if (hasOverflow) {
          // Show scrollbar when content overflows (content is hidden)
          container.classList.add('has-overflow');
        } else {
          // Keep scrollbar hidden if content fits
          container.classList.remove('has-overflow');
        }
      };
      
      // Check immediately after enabling scroll
      requestAnimationFrame(() => {
        checkOverflow();
        
        // Check again after a short delay
        setTimeout(() => {
          checkOverflow();
        }, 100);
        
        // Check again after content settles
        setTimeout(() => {
          checkOverflow();
        }, 300);
        
        // Final check after all animations
        setTimeout(() => {
          checkOverflow();
        }, 600);
      });
      
      // Listen for window resize to re-check overflow
      if (typeof window !== 'undefined') {
        const handleResize = () => {
          setTimeout(() => {
            checkOverflow();
          }, 100);
        };
        window.addEventListener('resize', handleResize);
        
        // Also observe container size changes
        if (window.ResizeObserver) {
          const resizeObserver = new ResizeObserver(() => {
            setTimeout(() => {
              checkOverflow();
            }, 50);
          });
          resizeObserver.observe(container);
        }
      }
    }
  }

  private updateStaffLayoutVisibility(): void {
    const url = this.router.url;
    // Hide staff-layout when on delivery layout routes
    this.showStaffLayout.set(!url.includes('/staff-delivery-layout'));
  }

  protected isSignInPage(): boolean {
    return this.router.url === '/' || this.router.url === '';
  }

  protected prepareRoute(outlet: RouterOutlet) {
    if (this.router.url.startsWith('/register')) {
      return 'NoAnimation';
    }
    return outlet?.activatedRouteData?.['animation'] ?? 'Default';
  }

  private scrollToTopOnNavigation(): void {
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    this.routeContainer?.nativeElement?.scrollTo({ top: 0, behavior: 'auto' });
  }

  /**
   * Initialize global click sound handler
   */
  private initializeClickSound(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Create click handler that plays sound on every click
    this.clickHandler = (event: MouseEvent) => {
      // Play sound on any click
      this.tapSoundService.play();
    };

    // Add event listener to document for all clicks
    document.addEventListener('click', this.clickHandler, true);
  }

  /**
   * Clean up click sound handler on destroy
   */
  ngOnDestroy(): void {
    if (this.clickHandler && isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.clickHandler, true);
    }
  }
}
